/**
 * Cognito + API configuration for the (app) auth flow.
 *
 * Every value is a PUBLIC identifier (User Pool id, client id, Identity Pool
 * id, API base URL) that ships to the browser anyway, so the literal dev
 * defaults below are safe to commit. Deploy overrides them with the canonical
 * SSM values by injecting `NEXT_PUBLIC_*` at build time (see
 * .github/workflows/deploy.yml) — Next inlines these at build, so the
 * references must stay static (`process.env.NEXT_PUBLIC_x`, never dynamic).
 *
 * This module is (app)-only. The (marketing) route group must never import it
 * (the auth SDKs would blow the marketing bundle budget) — enforced by the
 * no-restricted-imports wall in eslint.config.mjs.
 */

export interface AuthConfig {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  /** API Gateway base URL, no trailing slash. */
  apiBaseUrl: string;
  /** Cognito Hosted UI base URL (Google IdP sign-in), no trailing slash. */
  hostedUiDomain: string;
}

function trimTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export const authConfig: AuthConfig = {
  region: process.env.NEXT_PUBLIC_AWS_REGION ?? 'us-east-1',
  userPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID ?? 'us-east-1_gNqUwYZkc',
  userPoolClientId:
    process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID ?? '5d9tld7d536f0iaa87jhjqc1sh',
  identityPoolId:
    process.env.NEXT_PUBLIC_COGNITO_IDENTITY_POOL_ID ??
    'us-east-1:af1ce21e-6893-4db9-bba0-f12f822b479c',
  apiBaseUrl: trimTrailingSlash(
    process.env.NEXT_PUBLIC_API_URL ?? 'https://api.dev.simbay.app',
  ),
  hostedUiDomain: trimTrailingSlash(
    process.env.NEXT_PUBLIC_COGNITO_HOSTED_UI_DOMAIN ??
      'https://simbay-dev.auth.us-east-1.amazoncognito.com',
  ),
};

/**
 * The Identity Pool "logins" provider key for our User Pool. Passing the User
 * Pool id-token under this key links a User Pool account to the (cached)
 * guest IdentityId — the mechanism that keeps the uid stable across signup.
 */
export function userPoolLoginKey(config: AuthConfig = authConfig): string {
  return `cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;
}
